import { useState, useEffect } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Button, Separator, Spinner, Text, XStack, YStack } from 'tamagui';
import { useMailFolders } from '../mail/hooks';
import { useCategorizeFolder } from '../hooks/use-categorize-folder';
import type { CategorizeFolderResult } from '../hooks/use-categorize-folder';
import type { MailFolder } from '@mrrmlab/api-client';

export function MailFoldersRoute() {
  const { accountId } = useParams({ from: '/mail/$accountId/folders' });
  const navigate = useNavigate();
  const { data: folders, isLoading } = useMailFolders(accountId);
  const { run, cancel, running, progress, lastActivityAt } = useCategorizeFolder();
  const [activeFolder, setActiveFolder] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, CategorizeFolderResult>>({});
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [running]);

  async function handleCategorize(folderName: string) {
    setActiveFolder(folderName);
    const result = await run(accountId, folderName);
    setActiveFolder(null);
    if (result) setResults((r) => ({ ...r, [folderName]: result }));
  }

  if (isLoading) {
    return (
      <YStack flex={1} alignItems="center" justifyContent="center" padding="$6">
        <Spinner size="large" />
      </YStack>
    );
  }

  return (
    <YStack padding="$4" gap="$2" maxWidth={480}>
      <Text fontSize="$6" fontWeight="700" marginBottom="$2">Ordner</Text>
      <YStack>
        {folders?.map((folder, i) => (
          <YStack key={folder.name}>
            {i > 0 && <Separator />}
            <FolderRow
              folder={folder}
              onOpen={() =>
                navigate({
                  to: '/mail/$accountId/messages',
                  params: { accountId },
                  search: { folder: folder.name },
                })
              }
              onCategorize={() => handleCategorize(folder.name)}
              onCancel={cancel}
              isRunning={running && activeFolder === folder.name}
              isDisabled={running}
              progress={running && activeFolder === folder.name ? progress : null}
              idleSecs={running && activeFolder === folder.name && lastActivityAt ? Math.floor((now - lastActivityAt) / 1000) : null}
              result={results[folder.name] ?? null}
            />
          </YStack>
        ))}
      </YStack>
    </YStack>
  );
}

function FolderRow({
  folder,
  onOpen,
  onCategorize,
  onCancel,
  isRunning,
  isDisabled,
  progress,
  idleSecs,
  result,
}: {
  folder: MailFolder;
  onOpen: () => void;
  onCategorize: () => void;
  onCancel: () => void;
  isRunning: boolean;
  isDisabled: boolean;
  progress: { categorized: number; target: number } | null;
  idleSecs: number | null;
  result: { checked: number; categorized: number } | null;
}) {
  const isStuck = idleSecs !== null && idleSecs >= 20;

  return (
    <YStack>
      <XStack paddingVertical="$3" alignItems="center" borderRadius="$3" gap="$2">
        <Text flex={1} fontSize="$5" cursor="pointer" hoverStyle={{ color: '$color11' }} onPress={onOpen}>
          {folder.name}
        </Text>
        {isRunning ? (
          <XStack gap="$2" alignItems="center">
            <Spinner size="small" color={isStuck ? '$orange10' : '$color10'} />
            <Button size="$2" chromeless onPress={onCancel} color="$red10">Abbrechen</Button>
          </XStack>
        ) : (
          <Button size="$2" chromeless onPress={onCategorize} disabled={isDisabled} color="$color10">
            Kategorisieren
          </Button>
        )}
        <Text color="$color10" cursor="pointer" onPress={onOpen}>›</Text>
      </XStack>
      {isRunning ? (
        <YStack paddingBottom="$2" gap="$1">
          {progress ? (
            <Text fontSize="$2" color="$color10">{progress.categorized} kategorisiert, {progress.target} geprüft…</Text>
          ) : (
            <Text fontSize="$2" color="$color10">Starte…</Text>
          )}
          {isStuck ? (
            <Text fontSize="$2" color="$orange10">Keine Aktivität seit {idleSecs}s — möglicherweise aufgehängt</Text>
          ) : idleSecs !== null && idleSecs >= 5 ? (
            <Text fontSize="$2" color="$color10">Letzte Aktivität vor {idleSecs}s</Text>
          ) : null}
        </YStack>
      ) : result ? (
        <Text fontSize="$2" color={result.categorized > 0 ? '$green10' : '$color10'} paddingBottom="$2">
          {result.categorized > 0
            ? `${result.categorized} von ${result.checked} neu getaggt`
            : result.checked > 0
            ? `${result.checked} geprüft — alle bereits getaggt`
            : 'Keine Nachrichten gefunden'}
        </Text>
      ) : null}
    </YStack>
  );
}
